(() => {
  const ROOT_SELECTOR = '[data-tcg-collection-search-root]';

  function pageTokens(totalPages, currentPage) {
    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const visible = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const tokens = [];
    visible.forEach((page, index) => {
      if (index && page - visible[index - 1] > 1) tokens.push('ellipsis');
      tokens.push(page);
    });
    return tokens;
  }

  function paginationItem(content, className = '') {
    const item = document.createElement('span');
    if (className) item.className = className;
    item.append(content);
    return item;
  }

  function initializeCollectionSearch(root) {
    if (!root || root.dataset.collectionSearchInitialized === 'true') return;

    const searchInput = root.querySelector('[data-tcg-card-search-input], [data-sealed-search]');
    const searchForm = root.querySelector('[data-tcg-card-search], [data-sealed-search-form]');
    const status = root.querySelector('[data-tcg-search-status], [data-sealed-search-status]');
    const noResults = root.querySelector('[data-tcg-no-results], [data-sealed-no-results]');
    const grid = root.querySelector('[data-tcg-product-grid], [data-sealed-grid]');
    const resultsPerPage = root.querySelector('[data-results-per-page]');
    const serverPagination = root.querySelector('[data-collection-server-pagination]');
    const searchPagination = root.querySelector('[data-collection-search-pagination]');
    const cardSelector = grid?.matches('[data-sealed-grid]') ? '[data-sealed-card]' : '[data-tcg-product-card]';

    if (!searchInput || !status || !noResults || !grid || !searchPagination) return;
    root.dataset.collectionSearchInitialized = 'true';

    const originalCards = [...grid.querySelectorAll(cardSelector)];
    const resultLabel = root.dataset.collectionSearchLabel || 'product';
    const pluralLabel = `${resultLabel}s`;
    const collectionCount = Number(root.dataset.collectionSearchCount || 0);
    let allCardsPromise;
    let matchedCards = [];
    let searchPage = 1;
    let activeRequest = 0;
    let debounceTimer;

    function resultPageSize() {
      return Math.max(1, Number(resultsPerPage?.value || originalCards.length || 20));
    }

    function fetchUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('view', root.dataset.collectionSearchView);
      url.searchParams.set('page', String(page));
      return url;
    }

    async function fetchPage(page) {
      const response = await fetch(fetchUrl(page).toString(), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Collection search request failed (${response.status}).`);
      const html = await response.text();
      const documentCopy = new DOMParser().parseFromString(html, 'text/html');
      const fetchedRoot = documentCopy.querySelector(ROOT_SELECTOR);
      const fetchedGrid = fetchedRoot?.querySelector('[data-tcg-product-grid], [data-sealed-grid]');
      if (!fetchedGrid) throw new Error('Collection search results were not found in the response.');
      return {
        cards: [...fetchedGrid.querySelectorAll(cardSelector)],
        pages: Math.max(1, Number(fetchedGrid.dataset.collectionSearchPages || 1))
      };
    }

    async function loadAllCards() {
      if (!allCardsPromise) {
        allCardsPromise = (async () => {
          const firstPage = await fetchPage(1);
          if (firstPage.pages === 1) return firstPage.cards;
          const remainingPages = await Promise.all(
            Array.from({ length: firstPage.pages - 1 }, (_, index) => fetchPage(index + 2))
          );
          return firstPage.cards.concat(...remainingPages.map((page) => page.cards));
        })().catch((error) => {
          allCardsPromise = undefined;
          throw error;
        });
      }
      return allCardsPromise;
    }

    function paginationButton(label, page, className = '') {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.dataset.collectionSearchPage = String(page);
      button.setAttribute('aria-label', label === String(page) ? `Page ${page}` : `${label} page`);
      return paginationItem(button, className);
    }

    function renderPagination(totalPages) {
      searchPagination.replaceChildren();
      searchPagination.hidden = totalPages <= 1;
      if (totalPages <= 1) {
        searchPagination.style.setProperty('display', 'none', 'important');
      } else {
        searchPagination.style.removeProperty('display');
      }
      if (totalPages <= 1) return;

      if (searchPage > 1) searchPagination.append(paginationButton('Previous', searchPage - 1, 'prev'));
      pageTokens(totalPages, searchPage).forEach((token) => {
        if (token === 'ellipsis') {
          searchPagination.append(paginationItem(document.createTextNode('…'), 'deco'));
        } else if (token === searchPage) {
          const current = paginationItem(document.createTextNode(String(token)), 'current');
          current.setAttribute('aria-current', 'page');
          searchPagination.append(current);
        } else {
          searchPagination.append(paginationButton(String(token), token));
        }
      });
      if (searchPage < totalPages) searchPagination.append(paginationButton('Next', searchPage + 1, 'next'));
    }

    function renderSearchPage() {
      const pageSize = resultPageSize();
      const totalPages = Math.max(1, Math.ceil(matchedCards.length / pageSize));
      searchPage = Math.min(searchPage, totalPages);
      const start = (searchPage - 1) * pageSize;
      const pageCards = matchedCards.slice(start, start + pageSize);
      pageCards.forEach((card) => { card.hidden = false; });
      grid.replaceChildren(...pageCards);
      noResults.hidden = matchedCards.length !== 0;
      if (serverPagination) {
        serverPagination.hidden = true;
        serverPagination.style.setProperty('display', 'none', 'important');
      }
      renderPagination(totalPages);
      const label = matchedCards.length === 1 ? resultLabel : pluralLabel;
      status.textContent = `${matchedCards.length} matching ${label} in this collection${totalPages > 1 ? ` · Page ${searchPage} of ${totalPages}` : ''}`;
      root.dispatchEvent(new CustomEvent('tcg:collection-search-rendered', { detail: { cards: pageCards } }));
    }

    function restoreCollectionPage() {
      activeRequest += 1;
      originalCards.forEach((card) => { card.hidden = false; });
      grid.replaceChildren(...originalCards);
      if (serverPagination) {
        serverPagination.hidden = false;
        serverPagination.style.removeProperty('display');
      }
      searchPagination.hidden = true;
      searchPagination.style.setProperty('display', 'none', 'important');
      searchPagination.replaceChildren();
      noResults.hidden = true;
      status.textContent = '';
      root.removeAttribute('aria-busy');
    }

    async function runSearch() {
      const query = searchInput.value.trim().toLowerCase();
      if (!query) {
        restoreCollectionPage();
        return;
      }

      const request = ++activeRequest;
      root.setAttribute('aria-busy', 'true');
      status.textContent = `Searching all ${collectionCount || ''} ${collectionCount === 1 ? resultLabel : pluralLabel}…`.replace('all  ', 'all ');
      try {
        const collectionCards = await loadAllCards();
        if (request !== activeRequest || searchInput.value.trim().toLowerCase() !== query) return;
        matchedCards = collectionCards.filter((card) => (card.dataset.searchText || '').includes(query));
        searchPage = 1;
        renderSearchPage();
      } catch (error) {
        if (request !== activeRequest) return;
        matchedCards = originalCards.filter((card) => (card.dataset.searchText || '').includes(query));
        searchPage = 1;
        renderSearchPage();
        status.textContent = `The full collection could not be loaded. Showing ${matchedCards.length} matching ${matchedCards.length === 1 ? resultLabel : pluralLabel} from this page.`;
        console.warn('Collection-wide search fell back to the current page.', error);
      } finally {
        if (request === activeRequest) root.removeAttribute('aria-busy');
      }
    }

    searchForm?.addEventListener('submit', (event) => event.preventDefault());
    searchInput.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(runSearch, 250);
    });
    resultsPerPage?.addEventListener('change', () => {
      if (!searchInput.value.trim()) return;
      searchPage = 1;
      renderSearchPage();
    });
    searchPagination.addEventListener('click', (event) => {
      const button = event.target.closest('[data-collection-search-page]');
      if (!button) return;
      searchPage = Number(button.dataset.collectionSearchPage);
      renderSearchPage();
      searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initializeCollectionSearch(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeCollectionSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
